import type { Node as PMNode } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';

/**
 * Walks the current document and returns the live position + node for the
 * native-embed (or any node) carrying the given `localId` attribute.
 *
 * This is the single source of truth for "where is this embed RIGHT NOW",
 * independent of any cached position captured at hover-time. Cached `pos`
 * values can become stale if any transaction lands between hover and the
 * point at which we need to write to the doc; resolving by `localId`
 * defends against that drift.
 *
 * Returns `null` if no node with the matching `localId` is found, or if
 * the walk throws for any reason.
 */
export const resolveLatestPosByLocalId = (
	view: EditorView,
	localId: string,
): { node: PMNode; pos: number } | null => {
	if (!view || !localId) {
		return null;
	}

	let result: { node: PMNode; pos: number } | null = null;
	try {
		view.state.doc.descendants((node, pos) => {
			if (result) {
				return false;
			}
			if (node.attrs?.localId === localId) {
				result = { pos, node };
				return false;
			}
			return true;
		});
	} catch {
		return null;
	}

	return result;
};
